import type { DailyRoutineDetails } from '../dto/DailyRoutineDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import type { EmotionalProtocolLogRepository } from '../ports/EmotionalProtocolLogRepository';
import type { IdGenerator } from '../ports/IdGenerator';
import type { RoutinePersistenceTransactions } from '../ports/RoutinePersistenceTransactions';
import { fromEmotionalStateData } from '../../domain/emotion/EmotionalStateContent';
import { calculateDailyProgress } from '../../domain/progression/CalculateDailyProgress';
import { selectActiveSteps } from '../../domain/routine/DayModeRules';
import { DayMode, TaskStatus } from '../../domain/routine/enums';
import type { DailyRoutine, DailyStep } from '../../domain/routine/models';
import { addLogicalDays, isLogicalDate } from '../../domain/shared/LogicalDate';
import type { DailyRoutineId, DailyStepId, ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';

function clearStepOutcome(step: DailyStep) {
  const base = { ...step };
  delete base.startedAt;
  delete base.completedAt;
  delete base.postponedToDate;
  delete base.postponedToStepId;
  delete base.postponedFromStepId;
  return base;
}

export class PostponeDailyStep {
  public constructor(
    private readonly dailyRoutines: DailyRoutineRepository,
    private readonly dailySteps: DailyStepRepository,
    private readonly transactions: RoutinePersistenceTransactions,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
    private readonly emotionalProtocolLogs: EmotionalProtocolLogRepository,
  ) {}

  public async execute(
    profileId: ProfileId,
    sourceLogicalDate: string,
    stepId: DailyStepId,
    requestedTargetDate?: string,
  ): Promise<Result<DailyRoutineDetails, RoutineError>> {
    try {
      const targetDate = requestedTargetDate ?? addLogicalDays(sourceLogicalDate, 1);
      if (!isLogicalDate(targetDate) || targetDate <= sourceLogicalDate) {
        return failure(
          new RoutineError(
            'invalid_postponement_date',
            'La date de report doit être postérieure à la journée source.',
          ),
        );
      }

      const sourceRoutine = await this.dailyRoutines.findByLogicalDate(
        profileId,
        sourceLogicalDate,
      );
      if (!sourceRoutine) {
        return failure(new RoutineError('routine_not_found', 'Journée introuvable.'));
      }
      const sourceSteps = await this.dailySteps.listByRoutine(sourceRoutine.id);
      const current = sourceSteps.find((step) => step.id === stepId);
      if (!current || current.profileId !== profileId) {
        return failure(new RoutineError('step_not_found', 'Mission introuvable.'));
      }
      if (current.status === TaskStatus.postponed || current.postponedToStepId) {
        return failure(
          new RoutineError('step_already_postponed', 'Cette mission a déjà été reportée.'),
        );
      }

      const timestamp = this.clock.now().toISOString();
      const existingTargetRoutine = await this.dailyRoutines.findByLogicalDate(
        profileId,
        targetDate,
      );
      const targetRoutine: DailyRoutine =
        existingTargetRoutine ??
        ({
          id: this.ids.generate<DailyRoutineId>(),
          profileId,
          logicalDate: targetDate,
          dayMode: DayMode.normal,
          status: 'open',
          completionPercentage: 0,
          createdAt: timestamp,
          updatedAt: timestamp,
        } satisfies DailyRoutine);
      const targetSteps = existingTargetRoutine
        ? await this.dailySteps.listByRoutine(existingTargetRoutine.id)
        : [];
      const targetStepId = this.ids.generate<DailyStepId>();
      const targetStep: DailyStep = {
        ...clearStepOutcome(current),
        id: targetStepId,
        dailyRoutineId: targetRoutine.id,
        status: TaskStatus.pending,
        order: targetSteps.reduce((maximum, step) => Math.max(maximum, step.order), -1) + 1,
        createdAt: timestamp,
        updatedAt: timestamp,
        postponedFromStepId: current.id,
      };
      const sourceStep: DailyStep = {
        ...clearStepOutcome(current),
        status: TaskStatus.postponed,
        postponedToDate: targetDate,
        postponedToStepId: targetStepId,
        updatedAt: timestamp,
      };
      const updatedSourceSteps = sourceSteps.map((step) =>
        step.id === sourceStep.id ? sourceStep : step,
      );
      const sourceProgress = calculateDailyProgress(
        selectActiveSteps(updatedSourceSteps, sourceRoutine.dayMode),
      );
      const updatedSourceRoutine = {
        ...sourceRoutine,
        completionPercentage: sourceProgress.percentage,
        updatedAt: timestamp,
      };
      const targetProgress = calculateDailyProgress(
        selectActiveSteps([...targetSteps, targetStep], targetRoutine.dayMode),
      );
      const updatedTargetRoutine = {
        ...targetRoutine,
        completionPercentage: targetProgress.percentage,
        updatedAt: timestamp,
      };

      await this.transactions.postponeStep(
        updatedSourceRoutine,
        sourceStep,
        updatedTargetRoutine,
        targetStep,
      );
      const log = await this.emotionalProtocolLogs.findByLogicalDate(profileId, sourceLogicalDate);
      return success({
        routine: updatedSourceRoutine,
        steps: updatedSourceSteps,
        progress: sourceProgress,
        emotionalState: (log && fromEmotionalStateData(log.data)?.state) ?? null,
      });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
